import { useState, useEffect } from 'react';
import {
  Card, Table, Tag, Button, Space, InputNumber, Select, Alert, Empty,
} from 'antd';
import { CalculatorOutlined } from '@ant-design/icons';
import ReactEChartsCore from 'echarts-for-react';
import { storageCalc } from '../../services/api';

/**
 * 配储测算（售前工具）：
 * 输入典型日负荷/光伏曲线（默认取站内近 30 天遥测均值）与少量参数，
 * 估算各候选储能容量档位的年套利收益与静态回收年限，并给出推荐档位。
 * 诚实原则：结果为工程估算，仅供投资参考；峰谷价差、效率、DoD、单价等
 * 均为可改参数，默认值口径见后端 storageCalcService 注释。
 */

export default function StorageCalcPanel() {
  const [params, setParams] = useState({
    pv_capacity_kw: null,
    unit_price_yuan_per_kwh: 1200,
    price_peak: 0.85,
    price_valley: 0.25,
    efficiency: 0.9,
    depth_of_discharge: 0.9,
    cycles_per_day: 1,
  });
  const [capacities, setCapacities] = useState([100, 200, 500, 1000]);
  const [curves, setCurves] = useState(null); // 近30天典型日曲线（随测算请求一并提交）
  const [defaultsLoaded, setDefaultsLoaded] = useState(false);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  // 预填：分时电价/装机容量/候选档位/经验系数默认值 + 典型日曲线
  useEffect(() => {
    (async () => {
      try {
        const res = await storageCalc.defaults();
        const d = res.data || {};
        setParams((p) => ({
          ...p,
          pv_capacity_kw: d.pv_capacity_kw ?? p.pv_capacity_kw,
          price_peak: d.price_peak ?? p.price_peak,
          price_valley: d.price_valley ?? p.price_valley,
          unit_price_yuan_per_kwh: d.unit_price_yuan_per_kwh ?? p.unit_price_yuan_per_kwh,
          efficiency: d.efficiency ?? p.efficiency,
          depth_of_discharge: d.depth_of_discharge ?? p.depth_of_discharge,
        }));
        if (Array.isArray(d.capacities_kwh) && d.capacities_kwh.length) setCapacities(d.capacities_kwh);
        setCurves(d.curves || null);
      } catch (err) {
        // 拦截器已统一提示
      } finally {
        setDefaultsLoaded(true);
      }
    })();
  }, []);

  const setParam = (key) => (v) => setParams((p) => ({ ...p, [key]: v }));

  const handleCalc = async () => {
    setLoading(true);
    try {
      const res = await storageCalc.calc({
        ...params,
        capacities_kwh: capacities,
        load_curve: curves?.load_curve || undefined,
        pv_curve: curves?.pv_curve || undefined,
      });
      setResult(res.data);
    } catch (err) {
      // 拦截器已统一提示
    } finally {
      setLoading(false);
    }
  };

  const fmt = (v) => (v == null ? '-' : Number(v).toLocaleString('zh-CN', { maximumFractionDigits: 2 }));

  const columns = [
    { title: '容量档位(kWh)', dataIndex: 'capacity_kwh' },
    { title: '总投资(元)', dataIndex: 'investment_yuan', render: fmt },
    { title: '日放电电量(kWh)', dataIndex: 'daily_discharge_kwh', render: fmt },
    {
      title: '峰段利用率', dataIndex: 'utilization_pct',
      render: (v) => (v == null ? '未校验' : `${v}%`),
    },
    { title: '年套利收益(元)', dataIndex: 'annual_revenue_yuan', render: fmt },
    {
      title: '静态回收期(年)', dataIndex: 'payback_years',
      render: (v) => (v == null ? '-' : v),
    },
    {
      title: '敏感性（价差±10%）', dataIndex: 'sensitivity',
      render: (s) => (s
        ? `回收期 ${s.price_down_10pct_payback_years ?? '-'} ~ ${s.price_up_10pct_payback_years ?? '-'} 年`
        : '-'),
    },
    {
      title: '推荐', key: 'recommend', width: 80,
      render: (_, row) => (
        result?.recommendation?.capacity_kwh === row.capacity_kwh
          ? <Tag color="green">推荐</Tag>
          : null
      ),
    },
  ];

  // 对比图：年套利收益（柱）+ 静态回收期（线，右轴）
  const chartOption = () => ({
    tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
    legend: { data: ['年套利收益(元)', '静态回收期(年)'] },
    grid: { left: '3%', right: '4%', bottom: '3%', containLabel: true },
    xAxis: {
      type: 'category',
      data: (result?.results || []).map((r) => `${r.capacity_kwh} kWh`),
    },
    yAxis: [
      { type: 'value', name: '元' },
      { type: 'value', name: '年' },
    ],
    series: [
      {
        name: '年套利收益(元)',
        type: 'bar',
        barWidth: 40,
        itemStyle: { color: '#1890ff' },
        data: (result?.results || []).map((r) => r.annual_revenue_yuan),
      },
      {
        name: '静态回收期(年)',
        type: 'line',
        yAxisIndex: 1,
        itemStyle: { color: '#fa8c16' },
        data: (result?.results || []).map((r) => r.payback_years),
      },
    ],
  });

  // 曲线来源说明（诚实标注覆盖范围与截断情况）
  const curveNote = () => {
    if (!curves) return '未获取到站内遥测曲线：本次测算将不做峰段消纳约束，收益按满充满放口径。';
    if (!curves.sample_count) return '近 30 天暂无遥测数据：本次测算将不做峰段消纳约束。';
    const range = `${curves.coverage_start || '?'} ~ ${curves.coverage_end || '?'}`;
    return `负荷/光伏典型日曲线取自站内遥测均值（样本 ${curves.sample_count} 条，覆盖 ${range}）`
      + (curves.truncated ? '，数据量超限仅覆盖最近一段时期' : '。');
  };

  return (
    <div>
      <Alert
        style={{ marginBottom: 16 }}
        type="info"
        showIcon
        message="测算结果为估算，仅供投资参考"
        description="收益口径为理想化的&quot;谷充峰放&quot;套利估算，未计电池衰减、运维费、辅助用电、需量电费及光伏消纳收益；单价/效率/DoD 等为市场行情经验值，均可按实际项目调整。"
      />

      <Card title="测算参数" size="small" style={{ marginBottom: 16 }}>
        <Space wrap size={[16, 12]}>
          <span>光伏装机容量(kW) <InputNumber min={0} value={params.pv_capacity_kw} onChange={setParam('pv_capacity_kw')} style={{ width: 120 }} /></span>
          <span>储能单价(元/kWh) <InputNumber min={1} value={params.unit_price_yuan_per_kwh} onChange={setParam('unit_price_yuan_per_kwh')} style={{ width: 120 }} /></span>
          <span>峰电价(元/kWh) <InputNumber min={0} step={0.01} value={params.price_peak} onChange={setParam('price_peak')} style={{ width: 100 }} /></span>
          <span>谷电价(元/kWh) <InputNumber min={0} step={0.01} value={params.price_valley} onChange={setParam('price_valley')} style={{ width: 100 }} /></span>
          <span>日循环次数 <InputNumber min={1} max={2} value={params.cycles_per_day} onChange={setParam('cycles_per_day')} style={{ width: 70 }} /></span>
          <span>综合效率 <InputNumber min={0.5} max={1} step={0.01} value={params.efficiency} onChange={setParam('efficiency')} style={{ width: 80 }} /></span>
          <span>放电深度DoD <InputNumber min={0.1} max={1} step={0.05} value={params.depth_of_discharge} onChange={setParam('depth_of_discharge')} style={{ width: 80 }} /></span>
        </Space>
        <Space wrap style={{ marginTop: 12, width: '100%' }}>
          <span style={{ flex: 1, minWidth: 320 }}>
            候选容量档位(kWh)&nbsp;
            <Select
              mode="tags"
              style={{ minWidth: 280, width: '60%' }}
              value={capacities.map(String)}
              onChange={(vals) => setCapacities(vals.map((v) => parseFloat(v)).filter((v) => Number.isFinite(v) && v > 0))}
              placeholder="输入容量后回车，如 100 / 200 / 500"
              open={false}
              suffixIcon={null}
            />
          </span>
          <Button type="primary" icon={<CalculatorOutlined />} loading={loading} onClick={handleCalc} disabled={!defaultsLoaded || capacities.length === 0}>
            开始测算
          </Button>
        </Space>
        <div style={{ fontSize: 12, color: '#999', marginTop: 8 }}>{curveNote()}</div>
      </Card>

      {!result ? (
        <Card size="small"><Empty description="请确认参数后点击开始测算" /></Card>
      ) : (
        <>
          {result.recommendation && (
            <Alert
              style={{ marginBottom: 16 }}
              type={result.recommendation.capacity_kwh != null ? 'success' : 'warning'}
              showIcon
              message={result.recommendation.capacity_kwh != null
                ? `推荐档位：${result.recommendation.capacity_kwh} kWh`
                : '暂无推荐档位'}
              description={`${result.recommendation.reason}（${result.disclaimer}）`}
            />
          )}
          <Card title="档位对比" size="small" style={{ marginBottom: 16 }}>
            <ReactEChartsCore option={chartOption()} style={{ height: 320 }} notMerge />
          </Card>
          <Card title="测算结果明细" size="small">
            <Table
              columns={columns}
              dataSource={result.results.map((r) => ({ ...r, key: r.capacity_kwh }))}
              size="small"
              pagination={false}
            />
            <div style={{ fontSize: 12, color: '#999', marginTop: 8 }}>{result.basis}</div>
          </Card>
        </>
      )}
    </div>
  );
}
